"use client";

import { useState } from "react";

export interface ThumbRailCard {
  key: string;
  kind: "cover" | "species" | "closing";
  photoUrl: string | null;
}

export interface EditorThumbRailProps {
  cards: ThumbRailCard[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onAddSpeciesSlide: () => void;
  closingEnabled: boolean;
  onToggleClosing: () => void;
  /** Drag-and-drop reorder — cover/closing are pinned and never draggable. */
  reorderSlides: (fromId: string, toId: string) => void;
}

export default function EditorThumbRail({
  cards, activeIndex, setActiveIndex, onAddSpeciesSlide, closingEnabled, onToggleClosing, reorderSlides,
}: EditorThumbRailProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [draggedKey, setDraggedKey] = useState<string | null>(null);

  return (
    <div className="ced-thumb-rail">
      <div className="ced-thumb-rail-scroll">
        {cards.map((card, i) => {
          const reorderable = card.kind === "species";
          return (
            <button
              key={card.key}
              type="button"
              className={`ced-thumb${i === activeIndex ? " ced-thumb--active" : ""}${draggedKey === card.key ? " ced-thumb--dragging" : ""}`}
              onClick={() => setActiveIndex(i)}
              aria-label={`Slide ${i + 1}`}
              draggable={reorderable}
              onDragStart={reorderable ? (e) => { setDraggedKey(card.key); e.dataTransfer.effectAllowed = "move"; } : undefined}
              onDragEnd={reorderable ? () => setDraggedKey(null) : undefined}
              onDragOver={reorderable ? (e) => { if (draggedKey && draggedKey !== card.key) e.preventDefault(); } : undefined}
              onDrop={reorderable ? (e) => {
                e.preventDefault();
                if (draggedKey && draggedKey !== card.key) reorderSlides(draggedKey, card.key);
                setDraggedKey(null);
              } : undefined}
            >
              {card.photoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={card.photoUrl} alt="" />
              ) : (
                <span className={`ced-thumb-empty ced-thumb-empty--${card.kind}`} />
              )}
            </button>
          );
        })}
      </div>
      {/* Kept outside the scrollable strip above — overflow-x:auto on that strip
          implicitly clips overflow-y too, which was cutting off (and eating clicks
          meant for) this popup when it tried to render above the rail. */}
      <div className="ced-rail-add-wrap">
        <button type="button" className="ced-rail-add" onClick={() => setMenuOpen((v) => !v)} aria-label="Add slide">+</button>
        {menuOpen && (
          <div className="ced-rail-menu">
            <button type="button" onClick={() => { onAddSpeciesSlide(); setMenuOpen(false); }}>
              + Custom slide<span className="ced-rail-menu-sub">species, plant…</span>
            </button>
            <button type="button" onClick={() => { onToggleClosing(); setMenuOpen(false); }}>
              {closingEnabled ? "− Remove finish slide" : "+ Finish slide"}<span className="ced-rail-menu-sub">outro</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
